import { useState } from "react";
import {
  Activity,
  BarChart3,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  CreditCard,
  FileClock,
  LayoutDashboard,
  Mail,
  Menu,
  RefreshCw,
  Search,
  Settings2,
  Shield,
  Sparkles,
  Users,
  X,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Command center",
    items: [{ id: "overview", label: "Overview", icon: LayoutDashboard }],
  },
  {
    label: "Revenue & users",
    items: [
      { id: "users", label: "Users", icon: Users },
      { id: "payments", label: "Payments", icon: CreditCard },
      { id: "transactions", label: "Transactions", icon: Activity },
    ],
  },
  {
    label: "Recruiter operations",
    items: [
      { id: "recruiters", label: "Recruiters", icon: BriefcaseBusiness },
      { id: "resumes", label: "Resume vault", icon: FileClock },
    ],
  },
  {
    label: "AI & product",
    items: [
      { id: "ai-costs", label: "AI costs", icon: Sparkles },
      { id: "skill-paths", label: "Skill paths", icon: BarChart3 },
    ],
  },
  {
    label: "Growth & comms",
    items: [
      { id: "contact", label: "Contact inbox", icon: Mail },
      { id: "emails", label: "Email delivery", icon: Mail },
      { id: "email-campaigns", label: "Campaigns", icon: Search },
    ],
  },
];

const TAB_LABELS = Object.fromEntries(
  NAV_GROUPS.flatMap((group) => group.items.map((item) => [item.id, item.label])),
);

const DATE_RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "quarter", label: "Quarter" },
  { value: "all", label: "All time" },
];

function formatRefreshTime(value) {
  if (!value) return "Waiting for first sync";
  return `Updated ${value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function AdminShell({
  tab,
  setTab,
  navigate,
  onRefresh,
  refreshing = false,
  refreshedAt,
  dateRange,
  setDateRange,
  children,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  const chooseTab = (nextTab) => {
    setTab(nextTab);
    setMobileOpen(false);
  };

  return (
    <div className="admin-shell ap-root" data-testid="admin-shell">
      <aside className={`admin-sidebar ${mobileOpen ? "is-open" : ""}`} aria-label="Admin navigation">
        <div className="admin-sidebar__brand">
          <div className="admin-brand-mark"><Shield className="h-4 w-4" /></div>
          <div>
            <p className="admin-brand-name">PlacementOS</p>
            <p className="admin-brand-subtitle">Operations console</p>
          </div>
          <button type="button" className="admin-icon-button admin-mobile-close" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="admin-nav" aria-label="Admin sections">
          {NAV_GROUPS.map((group) => (
            <div className="admin-nav-group" key={group.label}>
              <p className="admin-nav-label">{group.label}</p>
              {group.items.map(({ id, label, icon: Icon }) => (
                <button
                  type="button"
                  key={id}
                  className={`admin-nav-item ${tab === id ? "is-active" : ""}`}
                  onClick={() => chooseTab(id)}
                  aria-current={tab === id ? "page" : undefined}
                  data-testid={`tab-${id}`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span>{label}</span>
                  {tab === id && <ChevronRight className="admin-nav-chevron h-3.5 w-3.5" aria-hidden="true" />}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <button type="button" className="admin-nav-item admin-nav-item--muted" onClick={() => navigate("/dashboard")} data-testid="admin-back-btn">
            <ChevronRight className="h-4 w-4 rotate-180" aria-hidden="true" />
            <span>Back to app</span>
          </button>
          <div className="admin-system-status"><span className="admin-status-dot" /> Admin services connected</div>
        </div>
      </aside>

      {mobileOpen && <button type="button" className="admin-sidebar-scrim" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}

      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar__left">
            <button type="button" className="admin-icon-button admin-mobile-menu" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <p className="admin-kicker">Admin workspace / {TAB_LABELS[tab] || "Overview"}</p>
              <h1 className="admin-page-title">{TAB_LABELS[tab] || "Overview"}</h1>
            </div>
          </div>
          <div className="admin-topbar__actions">
            <div className="admin-date-filter" role="group" aria-label="Reporting window">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              <div className="admin-date-filter__options">
                {DATE_RANGE_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`admin-date-filter__option ${dateRange === option.value ? "is-active" : ""}`}
                    aria-pressed={dateRange === option.value}
                    onClick={() => setDateRange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" className="admin-refresh-button" onClick={onRefresh} disabled={refreshing} aria-label="Refresh admin data">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="admin-refresh-label">Refresh</span>
            </button>
            <button type="button" className="admin-icon-button" aria-label="Open admin command search" onClick={() => setCommandOpen((value) => !value)}>
              <Settings2 className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="admin-workspace">
          <div className="admin-workspace-meta">
            <span className="admin-live-pill"><span className="admin-status-dot" /> Live snapshot</span>
            <span>{formatRefreshTime(refreshedAt)}</span>
            <span className="admin-meta-divider" />
            <span>All markets</span>
          </div>
          {commandOpen && (
            <div className="admin-command-panel" role="status">
              <Settings2 className="h-4 w-4 text-violet-600" />
              <span>The reporting window updates the overview cards, funnel, trends, and advanced reports. Choose All time to include the full production history.</span>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
